import type {
  ActingParty,
  ConveyedPersonAction,
  PersonInvolvement,
} from "../types/involvement.js";
import { isInvolvementHeldBy } from "./isInvolvementHeldBy.js";
import { isInvolvementLive } from "./isInvolvementLive.js";
import { personActionsFromInvolvementKindInRole } from "./personActionsFromInvolvementKindInRole.js";

/**
 * What a party's own involvements with a person convey to it, acting for one
 * organisation in the roles given.
 *
 * The one account of what a party's own involvements let it do. The gate's
 * rule is composed from this, so a page reading the same composition cannot
 * offer something the server would refuse, or hide something it would allow.
 *
 * Deliberately *not* the `actions` each involvement reports. Those are narrowed
 * by the holder's role, and this is a question about the requester: users in
 * the assessor and responsible user roles holding the same involvement do not
 * hold the same thing.
 *
 * This is the involvements alone, before allocation narrows them. Having
 * created one of the person's records conveys more, which is
 * `personActionsFromCreation`'s, and what a party may ask of the person is
 * `personActionsOf`.
 */
export const personActionsFromOwnInvolvements = (
  involvements: readonly Pick<
    PersonInvolvement,
    "kind" | "party" | "endedAt" | "expiresAt"
  >[],
  party: ActingParty,
  now: Date = new Date(),
): ConveyedPersonAction[] => [
  ...new Set(
    involvements
      .filter((involvement) => isInvolvementLive(involvement, now))
      .filter((involvement) => isInvolvementHeldBy(involvement, party))
      .flatMap((involvement) =>
        party.roles.flatMap((role) =>
          personActionsFromInvolvementKindInRole(involvement.kind, role),
        ),
      ),
  ),
];
